import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { API } from "api";
import { UTILS } from "utils";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import { calculateDeliveryTimeInMins } from "features/shipping_methods/helpers";
import { errorHandler } from "api/api/errorHandler";

const defaultState = {
  minOrder: "",
  deliveryFee: "",
  deliveryTime: {
    Hours: 0,
    Minutes: 0,
    Days: 0,
  },
};

const convertMinsToDeliveryTime = (minutesValue) => {
  const parsedMinutes =
    typeof minutesValue === "string"
      ? Number(minutesValue.replace(/[^\d]/g, ""))
      : Number(minutesValue);
  const totalMins = Number.isFinite(parsedMinutes) ? parsedMinutes : 0;
  const days = Math.floor(totalMins / 1440);
  const remainderAfterDays = totalMins % 1440;
  const hours = Math.floor(remainderAfterDays / 60);
  const minutes = remainderAfterDays % 60;
  return {
    Days: days,
    Hours: hours,
    Minutes: minutes,
  };
};

const EditDeliveryAreaModal = ({
  title,
  open,
  onClose,
  selectedArea,
  onSaved,
  onSave,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [loading, setLoading] = useState(false);
  const [selectedAreaState, setState] = useState(defaultState);

  const daysOptions = useMemo(
    () =>
      Array.from({ length: 8 }, (_, value) => ({
        label: `${value} ${t("Days")}`,
        value,
      })),
    [t]
  );
  const hourOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, value) => ({
        label: `${value} ${t("Hours")}`,
        value,
      })),
    [t]
  );
  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const value = index * 5;
        return {
          label: `${value} ${t("Minutes")}`,
          value,
        };
      }),
    [t]
  );

  useEffect(() => {
    if (!open || !selectedArea) return;
    setState({
      minOrder:
        selectedArea?.minOrder != null ? String(selectedArea.minOrder) : "",
      deliveryFee:
        selectedArea?.deliveryFee != null ? String(selectedArea.deliveryFee) : "",
      deliveryTime: convertMinsToDeliveryTime(selectedArea?.deliveryTime),
    });
  }, [open, selectedArea]);

  const handleValues = (key, value) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const handleTimeValues = (key, value) =>
    setState((prev) => ({
      ...prev,
      deliveryTime: {
        ...prev.deliveryTime,
        [key]: Number(value || 0),
      },
    }));

  const saveChanges = async () => {
    if (!selectedArea) return;
    if (!selectedAreaState.minOrder || Number(selectedAreaState.minOrder) < 0) {
      UTILS.errorNotification(t("Please enter minimum order amount"));
      return;
    }
    if (
      !selectedAreaState.deliveryFee ||
      Number(selectedAreaState.deliveryFee) < 0
    ) {
      UTILS.errorNotification(t("Please enter delivery fee"));
      return;
    }
    const deliveryTimeInMins = calculateDeliveryTimeInMins({
      deliveryTime: selectedAreaState.deliveryTime,
    });
    if (deliveryTimeInMins <= 0) {
      UTILS.errorNotification(t("Please choose delivery time"));
      return;
    }
    if (typeof onSave === "function") {
      const shouldClose = await onSave({
        selectedArea,
        minOrder: Number(selectedAreaState.minOrder),
        deliveryFee: Number(selectedAreaState.deliveryFee),
        deliveryTime: Number(deliveryTimeInMins),
      });
      if (shouldClose !== false) {
        onClose?.();
      }
      return;
    }

    const userDetail = UTILS.getCurrentUser();
    const areaPayload = {
      cityID: selectedArea?.cityID?._id,
      areaID: selectedArea?.areaID?._id,
      countryID: selectedArea?.countryID?._id,
      storeId: selectedArea?.storeId,
      brandId: selectedArea?.brandId,
    };
    if (
      !areaPayload.cityID ||
      !areaPayload.areaID ||
      !areaPayload.countryID ||
      !areaPayload.storeId ||
      !areaPayload.brandId
    ) {
      UTILS.errorNotification(t("Invalid area selected"));
      return;
    }

    try {
      setLoading(true);
      const result = await API.UPDATE_STORE_AREA({
        areas: [areaPayload],
        minOrder: Number(selectedAreaState.minOrder),
        deliveryFee: Number(selectedAreaState.deliveryFee),
        deliveryTime: Number(deliveryTimeInMins),
        token: userDetail?.token,
        language: currentLanguage,
      });
      if (result?.success === 1) {
        UTILS.successNotification(t(result?.message || "Area updated"));
        await onSaved?.();
        onClose?.();
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
    } catch (error) {
      UTILS.errorNotification(t("Please try again later!"));
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={loading}>
        {t("Discard Changes")}
      </AppOutlineButton>
      <AppButton variant="primary" onClick={saveChanges} loading={loading}>
        {t("Save Changes")}
      </AppButton>
    </div>
  );

  return (
    <AppModal title={title} open={open} onClose={onClose} width={650} footer={footer}>
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={selectedAreaState.minOrder}
          onInput={(e) =>
            handleValues("minOrder", UTILS.NumberConversion(e.target.value))
          }
        />
        <AppInput
          label={t("Delivery Fee")}
          value={selectedAreaState.deliveryFee}
          onInput={(e) =>
            handleValues("deliveryFee", UTILS.NumberConversion(e.target.value))
          }
        >
          <span>AED</span>
        </AppInput>
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={selectedAreaState.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) => handleTimeValues("Days", value)}
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={selectedAreaState.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) => handleTimeValues("Hours", value)}
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={selectedAreaState.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) => handleTimeValues("Minutes", value)}
        />
      </div>
    </AppModal>
  );
};

export default EditDeliveryAreaModal;